import { useContext, useEffect } from 'react'
import { ABTestGroupValues, AB_TESTING_NAMES } from '@playback-rewards/shared-libraries'

// constants
import {
  ANALYTICS_EMPTY_VALUE,
  ANALYTICS_EVENT,
  LOCAL_STORAGE,
  REACT_NATIVE_STATUS,
} from 'constants/index'
// contexts
import AuthContext from '../context/AuthContext'
import NativeAppContext from '../context/NativeApp/NativeAppContext'
// utils
import { sendGA4Event, sendRNMessage } from '../utils/utils'

/**
 * Reads the user's group for an experiment and reports that they were shown it.
 *
 * The group is taken from the user object as-is. Exposure is reported once per surface
 * per session, to the analytics endpoint and to AppsFlyer through the native app.
 *
 * @param {AB_TESTING_NAMES} experimentId - the experiment being rendered
 * @param {string} surface - the place rendering it, a value from EXPERIMENT_SURFACE
 * @returns {ABTestGroupValues | null} the user's group, or null when they have none
 */
const useExperiment = (
  experimentId: AB_TESTING_NAMES,
  surface: string
): ABTestGroupValues | null => {
  const { user } = useContext(AuthContext)
  const { nativeAppDetails } = useContext(NativeAppContext)

  const variant = user?.abTestGroups?.[experimentId]

  useEffect(() => {
    if (!user || !variant) {
      return
    }

    // The user id is part of the key so two accounts in the same tab each report once.
    const storageKey = `ab_exposure:${user.id}:${experimentId}:${surface}`
    if (sessionStorage.getItem(storageKey)) {
      return
    }
    sessionStorage.setItem(storageKey, 'true')

    const eventParams = {
      experiment_id: experimentId,
      variant,
      surface,
      web_version: process.env.REACT_APP_BUILD_VERSION || ANALYTICS_EMPTY_VALUE,
      native_app_version: nativeAppDetails?.versionName || ANALYTICS_EMPTY_VALUE,
    }

    sendGA4Event(
      { ...ANALYTICS_EVENT.AB_TEST_EXPOSURE, eventParams },
      {},
      localStorage.getItem(LOCAL_STORAGE.JWT_TOKEN)
    )

    sendRNMessage({
      status: REACT_NATIVE_STATUS.SEND_EVENT_APPSFLYER,
      eventName: ANALYTICS_EVENT.AB_TEST_EXPOSURE.eventName,
      eventValue: eventParams,
    })
    // nativeAppDetails is intentionally left out of the dependencies. It arrives after the
    // first render, and reacting to it would report the same exposure a second time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, variant, experimentId, surface])

  return variant ?? null
}

export default useExperiment
